/**
* conversation-window.util.ts
*
* Frontend mirror of `ConversationWindowUtil` in `@app/trendmulti-core`. The two must stay
* in step: the backend refuses the send, the frontend explains why before the agent types.
*/
import { Attendance } from '../models/attendance.model';
import { ChannelTypeEnum } from '../models/enum/channel.type';

/**
 * Tri-state of an attendance's service session (SPEC-2026-08 RF-11…RF-15).
 *
 * `NONE` is not "closed": it means the attendance never had a service session at all —
 * an agent, an integration or the public interface started the conversation. Composing is
 * allowed in that state. Only `EXPIRED` blocks the composer.
 */
export enum ConversationWindowStateEnum {
  NONE = 'NONE',
  OPEN = 'OPEN',
  EXPIRED = 'EXPIRED',
}

/** The only field of an attendance the window derives from. */
export type ConversationWindowAttendance = { expiredAt?: Date | string | null };

export class ConversationWindowUtil {

  /**
   * Derive the service-session state of an attendance.
   *
   *  - `expiredAt` absent  → `NONE`    — no session was ever opened
   *  - `expiredAt` > now   → `OPEN`    — a customer message opened a session that is still running
   *  - `expiredAt` <= now  → `EXPIRED` — the session elapsed
   *
   * Every channel other than RCS returns `NONE`, so no existing channel changes behaviour.
   *
   * The comparison is between raw instants — `expiredAt` is already an absolute instant sent
   * by the API, and shifting it into a time zone here would apply the offset a second time
   * and move the deadline by hours.
   *
   * An `expiredAt` that cannot be read as a date also returns `NONE`: an unreadable value
   * must never be the reason an agent is locked out of a conversation.
   */
  static stateOf(
    channelType: ChannelTypeEnum | string,
    attendance: Attendance | ConversationWindowAttendance | null | undefined,
  ): ConversationWindowStateEnum {
    if (channelType !== ChannelTypeEnum.RCS_API) {
      return ConversationWindowStateEnum.NONE;
    }

    const expiredAt = attendance?.expiredAt;
    if (expiredAt === null || expiredAt === undefined) {
      return ConversationWindowStateEnum.NONE;
    }

    const expiresAt: Date = expiredAt instanceof Date ? expiredAt : new Date(expiredAt);
    if (isNaN(expiresAt.getTime())) {
      return ConversationWindowStateEnum.NONE;
    }

    return expiresAt.getTime() > new Date().getTime()
      ? ConversationWindowStateEnum.OPEN
      : ConversationWindowStateEnum.EXPIRED;
  }

}
